import apiClient from './apiClient'

export type QuestionnaireAnswerValue = boolean | string | string[] | null

export type QuestionnaireAnswers = Record<string, QuestionnaireAnswerValue>

export type QuestionnaireOption = {
  value: string
  label: string
}

export type QuestionnaireQuestion = {
  id: string
  type: string
  label: string
  helperText?: string
  placeholder?: string
  section?: string
  options?: QuestionnaireOption[]
  uiMode?: 'action' | 'documents_link' | 'question'
  visibleIf?: Record<string, boolean | string>
}

export type QuestionnaireStatus = {
  totalVisible: number
  answeredVisible: number
  complete: boolean
}

export type QuestionnaireResult = {
  answers: QuestionnaireAnswers
  visible: QuestionnaireQuestion[]
  status: QuestionnaireStatus
  schema?: QuestionnaireQuestion[]
}

export type PayeIncomeItem = {
  id: string
  gross?: number
  payeWithheld?: number
  taxWithheld?: number
  amount?: number
  employer?: string
  payer?: string
  sourceName?: string
  createdAt?: string
}

export type IncomeBucket = {
  paye: PayeIncomeItem[]
  interest: PayeIncomeItem[]
  dividends: PayeIncomeItem[]
  other: PayeIncomeItem[]
}

export type CryptoTransaction = {
  id: string
  occurredAt: string
  asset: string
  type: string
  amount: number
  priceNzd: number
  feeNzd: number
  source?: string
}

export type ManualDocumentEvidenceLink = {
  mode: 'manual'
  supports?: string
  section?: string
  ir3Refs?: string[]
  summaryKey?: string | null
}

export type DocumentEvidenceLink = ManualDocumentEvidenceLink | { mode: 'none' } | null

export type DocumentEvidenceLinksPayload = ManualDocumentEvidenceLink[] | { mode: 'none' } | null

export type WorkspaceDocument = {
  id: string
  workspaceId: string
  filename: string
  originalName: string
  mimeType: string
  size: number
  docType: string
  status: string
  uploadedAt: string
  donationAmount?: number
  evidenceLink?: DocumentEvidenceLink
  evidenceLinks?: ManualDocumentEvidenceLink[]
}

export type EvidenceLinkOption = {
  key: string
  label: string
  value: DocumentEvidenceLink
}

export type DocumentsResult = {
  items: WorkspaceDocument[]
  evidenceLinkOptions: EvidenceLinkOption[]
}

export type ChecklistItem = {
  docType: string
  label: string
  reason: string
  applicable: boolean
  required: boolean
  received: boolean
  status: 'missing' | 'received' | 'not_applicable'
  count: number
}

export type AuditEvent = {
  id: string
  at: string
  action: string
  actor: string
  category: string
  label: string
  details?: string | null
  meta?: Record<string, unknown>
}

export type AuditSummary = {
  totalEvents: number
  latestEventAt: string | null
  byCategory: Record<string, number>
}

export type AuditResult = {
  events: AuditEvent[]
  summary: AuditSummary
  overallSummary: AuditSummary
  availableCategories: string[]
  filters: {
    category: string | null
    q: string | null
    limit: number | null
  }
}

export type ReviewEvidenceItem = {
  documentId: string
  document: string
  documentType: string
  supports: string
  section: string
  ir3Refs: string[]
  summaryKey: string | null
  uploadedAt: string
  status: string
  linkMode?: 'auto' | 'manual'
}

export type WarningEvidenceOverride = {
  mode: 'manual' | 'none'
  documentIds: string[]
}

export type ReviewWarning = {
  code: string
  severity: string
  message: string
  evidence?: ReviewEvidenceItem[]
  evidenceOverride?: WarningEvidenceOverride | null
}

export type ReviewTraceabilityItem = {
  ref: string
  label: string
  value: unknown
  note: string
  source: string
  evidenceCount: number
  traceStatus: 'evidenced' | 'explained' | 'unexplained'
  evidence: ReviewEvidenceItem[]
}

export type ReviewTraceabilityGap = {
  ref: string
  label: string
  reason: string
  severity: 'medium' | 'high'
  fieldFamily: string
  requestArea: string
  requestText: string
  suggestedDocTypes: string[]
}

export type ReviewTraceabilityFollowUpItem = {
  id: string
  ref: string
  label: string
  severity: 'medium' | 'high'
  fieldFamily: string
  requestArea: string
  requestText: string
  reason: string
  suggestedDocTypes: string[]
}

export type ReviewPayload = {
  readiness: {
    score: number
    status: 'strong' | 'review_needed' | 'needs_attention'
  }
  submissionReadiness: {
    status: 'ready_to_review' | 'action_needed'
    blockerCount: number
    questionnaire: QuestionnaireStatus
    documents: {
      applicableCount: number
      receivedCount: number
      items: Array<{
        docType: string
        label: string
        reason: string
        required: boolean
        received: boolean
      }>
    }
    blockers: Array<{
      code: string
      severity: string
      label: string
      message: string
      targetTab?: 'questionnaire' | 'documents' | 'ir3_summary'
      actionLabel?: string
    }>
    nextActions: string[]
    finalReviewChecklist: Array<{
      key: string
      label: string
      status: 'done' | 'review' | 'action_needed'
      detail: string
    }>
  }
  warnings: ReviewWarning[]
  assumptions: string[]
  evidence: ReviewEvidenceItem[]
  reviewerActionQueue: {
    handoffStatus: 'ready_for_handoff' | 'review_in_progress' | 'blocked'
    headline: string
    closureSummary: string
    totalTrackedCount: number
    totalCount: number
    resolvedCount: number
    highPriorityCount: number
    handoffBlockers: string[]
    remainingIssuesPack: {
      headline: string
      items: Array<{
        id: string
        title: string
        severity: 'high' | 'medium'
        requestArea: string
        requestText: string
        category: 'filing_readiness' | 'traceability' | 'review_warning' | 'assumption'
      }>
    }
    handoffPack?: {
      status: 'ready' | 'action_needed'
      summary: string
      nextStep: string
      checklist: Array<{
        key: string
        label: string
        status: 'done' | 'review' | 'action_needed'
        detail: string
      }>
    }
    finalSignoff?: {
      status: 'pending' | 'signed_off'
      signedOffAt: string | null
      signedOffBy: string | null
      overrideReason: string
      requiresOverride: boolean
      signedOffAgainstStatus: 'ready' | 'action_needed' | null
      isStale?: boolean
      staleReason?: string
      recoveryStep?: string
      summary: string
    }
    operatorHandoff?: {
      status: 'pending_reviewer_signoff' | 'stale_reviewer_signoff' | 'pending_operator_ack' | 'acknowledged' | 'stale_operator_handoff'
      acknowledgedAt: string | null
      acknowledgedBy: string | null
      signedOffAt: string | null
      note: string
      summary: string
      nextStep: string
      requiresReacknowledgement?: boolean
    }
    closureConfidence?: {
      status: 'blocked' | 'awaiting_signoff' | 'awaiting_operator_acknowledgement' | 'awaiting_filing_submission' | 'awaiting_filing_confirmation' | 'confirmed' | 'reopened'
      headline: string
      summary: string
      nextStep: string
      redoCheckpoints: Array<{
        key: 'sign_off' | 'operator_acknowledgement' | 'filing_confirmation'
        label: string
        detail: string
      }>
    }
    filingExecution?: {
      status: 'not_started' | 'submitted' | 'confirmed'
      submittedAt: string | null
      confirmedAt: string | null
      irdReference: string
      note: string
      summary: string
      nextStep: string
      blockedByHandoff: boolean
    }
    handoffTimeline?: {
      headline: string
      items: Array<{
        key: string
        status: 'done' | 'stale'
        label: string
        at: string | null
        by: string | null
        detail: string
      }>
    }
    categories: Array<{
      category: 'filing_readiness' | 'traceability' | 'review_warning' | 'assumption'
      label: string
      count: number
      highPriorityCount: number
    }>
    items: Array<{
      id: string
      sourceType: 'submission_blocker' | 'traceability_gap' | 'review_warning' | 'assumption'
      sourceKey: string
      severity: 'high' | 'medium'
      title: string
      detail: string
      requestText: string
      requestArea: string
      targetTab?: 'questionnaire' | 'documents' | 'ir3_summary'
      actionLabel?: string
      category: 'filing_readiness' | 'traceability' | 'review_warning' | 'assumption'
      supportState?: 'missing_support' | 'missing_input' | 'review_required' | 'assumed'
      actionType?: 'collect_document' | 'complete_input' | 'review_tax_position' | 'replace_assumption'
      resolutionStatus?: 'open' | 'resolved'
      resolvedAt?: string | null
      resolutionNote?: string
    }>
    resolvedItems: Array<{
      id: string
      sourceType: 'submission_blocker' | 'traceability_gap' | 'review_warning' | 'assumption'
      sourceKey: string
      severity: 'high' | 'medium'
      title: string
      detail: string
      requestText: string
      requestArea: string
      targetTab?: 'questionnaire' | 'documents' | 'ir3_summary'
      actionLabel?: string
      category: 'filing_readiness' | 'traceability' | 'review_warning' | 'assumption'
      supportState?: 'missing_support' | 'missing_input' | 'review_required' | 'assumed'
      actionType?: 'collect_document' | 'complete_input' | 'review_tax_position' | 'replace_assumption'
      resolutionStatus?: 'open' | 'resolved'
      resolvedAt?: string | null
      resolutionNote?: string
    }>
    recentlyResolved: Array<{
      id: string
      sourceType: 'submission_blocker' | 'traceability_gap' | 'review_warning' | 'assumption'
      sourceKey: string
      severity: 'high' | 'medium'
      title: string
      detail: string
      requestText: string
      requestArea: string
      targetTab?: 'questionnaire' | 'documents' | 'ir3_summary'
      actionLabel?: string
      category: 'filing_readiness' | 'traceability' | 'review_warning' | 'assumption'
      supportState?: 'missing_support' | 'missing_input' | 'review_required' | 'assumed'
      actionType?: 'collect_document' | 'complete_input' | 'review_tax_position' | 'replace_assumption'
      resolutionStatus?: 'open' | 'resolved'
      resolvedAt?: string | null
      resolutionNote?: string
    }>
    shortlistHeadline: string
    recentlyResolvedHeadline?: string
  }
  traceability: {
    keyFieldCount: number
    evidencedFieldCount: number
    explainedFieldCount: number
    items: ReviewTraceabilityItem[]
    gaps: ReviewTraceabilityGap[]
    followUpPack: {
      headline: string
      items: ReviewTraceabilityFollowUpItem[]
    }
  }
  summary: {
    donationAmount: number
    pieIncome: number
    pieTaxCredits: number
    extraTaxDeducted: number
    studentLoanRepayments: number
    provisionalTaxStatus: {
      threshold: number
      standardOptionUpliftRate: number
      relevant: boolean
      modeledResidualIncomeTax: number
      estimatedStandardOptionTax: number
      estimateBasis: string
    }
    studentLoanStatus: {
      hasStudentLoan: boolean
      hasStatement: boolean
      repaymentsEntered: number
      status: 'not_applicable' | 'ready' | 'partial' | 'needs_attention'
    }
  }
}

export type WorkspaceAdjustments = {
  donationAmount: number
  pieIncome: number
  pieTaxCredits: number
  extraTaxDeducted: number
  studentLoanRepayments: number
}

export type Ir3CalcResult = {
  map: Record<string, unknown>
  calc: Record<string, unknown>
  explanation?: {
    headline?: string
    bullets?: string[]
    fieldNotes?: Array<{ ref: string; note: string }>
  }
}

export type DraftExport = {
  csv: string
  trialBalanceCsv: string
  accountantCsv: string
  json: {
    workspace: {
      id: string
      taxYearStart: string
      taxYearEnd: string
      status?: string
      updatedAt?: string
    }
    generatedAt: string
    map: Record<string, unknown>
    calc: Record<string, unknown>
    explanation?: {
      headline?: string
      bullets?: string[]
      fieldNotes?: Array<{ ref: string; note: string }>
    }
    review?: ReviewPayload
  }
  explanation?: {
    headline?: string
    bullets?: string[]
    fieldNotes?: Array<{ ref: string; note: string }>
  }
  review?: ReviewPayload
  pdf: {
    title: string
    generatedAt: string
    mimeType: string
    filename: string
    bytesBase64: string
    sections: Array<{ name: string; values: Record<string, unknown> }>
  }
}

export const workspaceFlowsApi = {
  async getQuestionnaireSchema(): Promise<QuestionnaireQuestion[]> {
    const response = await apiClient.get('/questionnaire/schema')
    return response.data?.questions || []
  },

  async getQuestionnaire(workspaceId: string): Promise<QuestionnaireResult> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/questionnaire`)
    return response.data
  },

  async saveQuestionnaire(workspaceId: string, answers: QuestionnaireAnswers): Promise<QuestionnaireResult> {
    const response = await apiClient.put(`/workspaces/${workspaceId}/questionnaire`, { answers })
    return response.data
  },

  async getAdjustments(workspaceId: string): Promise<WorkspaceAdjustments> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/adjustments`)
    return response.data?.adjustments
  },

  async saveAdjustments(workspaceId: string, adjustments: WorkspaceAdjustments): Promise<WorkspaceAdjustments> {
    const response = await apiClient.put(`/workspaces/${workspaceId}/adjustments`, adjustments)
    return response.data?.adjustments
  },

  async getReview(workspaceId: string): Promise<ReviewPayload> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/review`)
    return response.data?.review
  },


  async saveReviewerActionStatus(
    workspaceId: string,
    actionId: string,
    payload: { status: 'open' | 'resolved'; title?: string; note?: string }
  ): Promise<ReviewPayload> {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/review/actions/${encodeURIComponent(actionId)}/status`,
      payload,
    )
    return response.data?.review
  },

  async saveWarningEvidenceOverride(
    workspaceId: string,
    warningCode: string,
    payload: { mode: 'auto' | 'manual' | 'none'; documentIds?: string[] }
  ): Promise<ReviewPayload> {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/review/warnings/${encodeURIComponent(warningCode)}/evidence`,
      payload,
    )
    return response.data?.review
  },

  async saveReviewerFinalSignoff(
    workspaceId: string,
    payload: { overrideReason?: string }
  ): Promise<ReviewPayload> {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/review/final-signoff`,
      payload,
    )
    return response.data?.review
  },

  async saveOperatorHandoffAck(
    workspaceId: string,
    payload: { note?: string }
  ): Promise<ReviewPayload> {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/review/operator-handoff`,
      payload,
    )
    return response.data?.review
  },

  async saveFilingCheckpoint(
    workspaceId: string,
    payload: { status: 'not_started' | 'submitted' | 'confirmed'; irdReference?: string; note?: string; submittedAt?: string; confirmedAt?: string }
  ): Promise<ReviewPayload> {
    const response = await apiClient.patch(
      `/workspaces/${workspaceId}/review/filing-checkpoint`,
      payload,
    )
    return response.data?.review
  },

  async addIncome(
    workspaceId: string,
    type: 'paye' | 'interest' | 'dividends' | 'other',
    payload: Record<string, unknown>
  ): Promise<PayeIncomeItem> {
    const response = await apiClient.post(`/workspaces/${workspaceId}/income/${type}`, payload)
    return response.data?.item
  },

  async listIncome(workspaceId: string): Promise<IncomeBucket> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/income`)
    return response.data?.income
  },

  async uploadDocument(workspaceId: string, file: File, docType: string, donationAmount?: number): Promise<WorkspaceDocument> {
    const form = new FormData()
    form.append('file', file)
    form.append('docType', docType)
    if (docType === 'donation_receipts' && typeof donationAmount === 'number' && !Number.isNaN(donationAmount)) {
      form.append('donationAmount', String(donationAmount))
    }
    const response = await apiClient.post(`/workspaces/${workspaceId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data?.document
  },

  async listDocuments(workspaceId: string): Promise<DocumentsResult> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/documents`)
    return {
      items: response.data?.items || [],
      evidenceLinkOptions: response.data?.evidenceLinkOptions || [],
    }
  },

  async updateDocumentDonationAmount(workspaceId: string, documentId: string, donationAmount: number): Promise<WorkspaceDocument> {
    const response = await apiClient.patch(`/workspaces/${workspaceId}/documents/${documentId}/donation-amount`, { donationAmount })
    return response.data?.document
  },

  async updateDocumentType(workspaceId: string, documentId: string, docType: string): Promise<WorkspaceDocument> {
    const response = await apiClient.patch(`/workspaces/${workspaceId}/documents/${documentId}/doc-type`, { docType })
    return response.data?.document
  },

  async updateDocumentEvidenceLink(workspaceId: string, documentId: string, evidenceLinks: DocumentEvidenceLinksPayload): Promise<WorkspaceDocument> {
    const response = await apiClient.patch(`/workspaces/${workspaceId}/documents/${documentId}/evidence-link`, { evidenceLinks })
    return response.data?.document
  },

  async deleteDocument(workspaceId: string, documentId: string): Promise<void> {
    await apiClient.delete(`/workspaces/${workspaceId}/documents/${documentId}`)
  },

  async getChecklist(workspaceId: string): Promise<ChecklistItem[]> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/checklist`)
    return response.data?.checklist || []
  },

  async getAudit(workspaceId: string, filters?: { category?: string | null; q?: string | null }): Promise<AuditResult> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/audit`, {
      params: {
        category: filters?.category || undefined,
        q: filters?.q || undefined,
      },
    })
    return response.data
  },

  async importCryptoCsv(workspaceId: string, csv: string): Promise<{ imported: number; total: number }> {
    const response = await apiClient.post(`/workspaces/${workspaceId}/crypto/import-csv`, { csv })
    return response.data
  },

  async listCryptoTransactions(workspaceId: string): Promise<CryptoTransaction[]> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/crypto/transactions`)
    return response.data?.items || []
  },

  async getIr3Calc(workspaceId: string): Promise<Ir3CalcResult> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/ir3/calc`)
    return response.data
  },

  async getDraftExport(workspaceId: string): Promise<DraftExport> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/export/draft`)
    return response.data
  },

  async downloadAccountantPack(workspaceId: string): Promise<{ filename: string; mimeType: string; bytes: ArrayBuffer }> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/export/accountant-pack`, {
      responseType: 'arraybuffer',
    })
    const disposition = String(response.headers['content-disposition'] || '')
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/) 
    return {
      filename: filenameMatch?.[1] || `accountant-pack-${workspaceId}.zip`,
      mimeType: String(response.headers['content-type'] || 'application/zip'),
      bytes: response.data,
    }
  },
}
